import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Search, 
  Filter, 
  Star, 
  Users, 
  Calendar, 
  TrendingUp, 
  TrendingDown,
  Eye,
  Heart,
  ExternalLink
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Project {
  id: string;
  title: string;
  logline: string;
  hero_image_url?: string;
  vertical: string;
  artie_score: number;
  forecasted_reach_90d: number;
  production_status: string;
  creator: string;
  reason_to_believe?: string;
  heuristic_tags?: string[];
  is_validated: boolean;
  category_density: {
    [key: string]: number;
  };
  tv_ranking_tier?: string;
  eligibility_score?: number;
  demographic_alignment?: number;
  genre_alignment?: number;
}

interface Campaign {
  id: string;
  name: string;
  requirements: {
    target_audience: string[];
    target_categories: string[];
    budget_usd: number;
    target_ecpm: number;
  };
}

interface PreferenceSignal {
  project_id: string;
  direction: 'up' | 'down';
}

const BrowseInventoryPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [preferenceSignals, setPreferenceSignals] = useState<PreferenceSignal[]>([]);
  const [filters, setFilters] = useState({
    search: '',
    vertical: '',
    production_status: '',
    min_reach: '',
    tv_ranking: ''
  });

  const campaignId = searchParams.get('campaign_id');

  useEffect(() => {
    if (campaignId) {
      fetchCampaignAndProjects();
    }
  }, [campaignId, filters]);

  const fetchCampaignAndProjects = async () => {
    try {
      setLoading(true);
      
      // Fetch campaign details
      const campaignData = await apiClient.get<Campaign>(`/api/v1/campaigns/${campaignId}`);
      setCampaign(campaignData);

      // Fetch eligible projects with ranking
      const params = new URLSearchParams({
        campaign_id: campaignId!,
        ...(filters.search && { search: filters.search }),
        ...(filters.vertical && { vertical: filters.vertical }),
        ...(filters.production_status && { production_status: filters.production_status }),
        ...(filters.min_reach && { min_reach: filters.min_reach }),
        ...(filters.tv_ranking && { tv_ranking: filters.tv_ranking })
      });

      const projectsData = await apiClient.get<{ projects: any[] }>(`/api/v1/brand-marketplace/eligible-projects?${params}`);
      setProjects(projectsData.projects || []);

      // Fetch existing preference signals
      const signalsData = await apiClient.get<PreferenceSignal[]>(`/api/v1/brand-marketplace/campaigns/${campaignId}/preference-signals`);
      setPreferenceSignals(signalsData);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load inventory data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreferenceSignal = async (projectId: string, direction: 'up' | 'down') => {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      // Safari fallback: Add Authorization header if token exists in sessionStorage
      const safariToken = sessionStorage.getItem('safari_auth_token');
      if (safariToken) {
        headers['Authorization'] = `Bearer ${safariToken}`;
      }
      
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/brand-marketplace/campaigns/${campaignId}/preference-signals`, {
        method: 'POST',
        headers,
        credentials: 'include', // Send cookies with requests
        body: JSON.stringify({
          project_id: projectId,
          direction
        })
      });

      if (response.ok) {
        // Update local state
        setPreferenceSignals(prev => {
          const filtered = prev.filter(signal => signal.project_id !== projectId);
          return [...filtered, { project_id: projectId, direction }];
        });

        toast({
          title: "Preference Recorded",
          description: `Marked project as ${direction === 'up' ? 'interested' : 'not interested'}`
        });
      }
    } catch (error) {
      console.error('Error recording preference:', error);
      toast({
        title: "Error",
        description: "Failed to record preference",
        variant: "destructive"
      });
    }
  };

  const openProjectDetails = (project: Project) => {
    setSelectedProject(project);
  };

  const viewPackage = (projectId: string) => {
    navigate(`/brand-marketplace/package?project_id=${projectId}&campaign_id=${campaignId}`);
  };

  const getProjectSignal = (projectId: string) => {
    return preferenceSignals.find(signal => signal.project_id === projectId);
  };

  const getTVRankingColor = (tier?: string) => {
    switch (tier) {
      case 'premium': return 'bg-purple-100 text-purple-800';
      case 'standard': return 'bg-blue-100 text-blue-800';
      case 'emerging': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Browse Inventory</h1>
              {campaign && (
                <p className="text-gray-600 mt-1">
                  Campaign: {campaign.name} • Budget: ${campaign.requirements.budget_usd.toLocaleString()}
                </p>
              )}
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="bg-green-50 text-green-700">
                {projects.length} Eligible Projects
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search projects..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filters.vertical} onValueChange={(value) => setFilters(prev => ({ ...prev, vertical: value }))}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Verticals" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Verticals</SelectItem>
                <SelectItem value="comedy">Comedy</SelectItem>
                <SelectItem value="drama">Drama</SelectItem>
                <SelectItem value="action">Action</SelectItem>
                <SelectItem value="documentary">Documentary</SelectItem>
                <SelectItem value="lifestyle">Lifestyle</SelectItem>
                <SelectItem value="tech">Tech</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.tv_ranking} onValueChange={(value) => setFilters(prev => ({ ...prev, tv_ranking: value }))}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Rankings" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Rankings</SelectItem>
                <SelectItem value="premium">Premium Tier</SelectItem>
                <SelectItem value="standard">Standard Tier</SelectItem>
                <SelectItem value="emerging">Emerging Tier</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Project Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {projects.map((project) => {
            const signal = getProjectSignal(project.id);
            return (
              <Card 
                key={project.id} 
                className="group hover:shadow-lg transition-shadow cursor-pointer relative overflow-hidden"
                onClick={() => openProjectDetails(project)}
              >
                {/* Hero Image */}
                <div className="aspect-video bg-gray-200 relative overflow-hidden">
                  {project.hero_image_url ? (
                    <img 
                      src={project.hero_image_url} 
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center">
                      <span className="text-white text-lg font-bold">
                        {project.title.charAt(0)}
                      </span>
                    </div>
                  )}
                  
                  {/* Preference Signals */}
                  <div className="absolute top-2 right-2 flex gap-1">
                    <Button
                      size="sm"
                      variant={signal?.direction === 'up' ? 'default' : 'secondary'}
                      className="h-8 w-8 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreferenceSignal(project.id, 'up');
                      }}
                    >
                      <TrendingUp className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant={signal?.direction === 'down' ? 'destructive' : 'secondary'}
                      className="h-8 w-8 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreferenceSignal(project.id, 'down');
                      }}
                    >
                      <TrendingDown className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Validated Badge */}
                  {project.is_validated && (
                    <div className="absolute top-2 left-2">
                      <Badge className="bg-green-100 text-green-800 border-green-200">
                        <Star className="h-3 w-3 mr-1" />
                        Validated
                      </Badge>
                    </div>
                  )}
                </div>

                <CardContent className="p-4">
                  {/* Title and Logline */}
                  <h3 className="font-semibold text-lg mb-2 line-clamp-1">{project.title}</h3>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">{project.logline}</p>

                  {/* Reason to Believe */}
                  {project.reason_to_believe && (
                    <p className="text-blue-600 text-sm font-medium mb-3 line-clamp-1">
                      {project.reason_to_believe}
                    </p>
                  )}

                  {/* Metrics */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Users className="h-4 w-4" />
                      <span>{formatNumber(project.forecasted_reach_90d)}</span>
                    </div>
                    {project.tv_ranking_tier && (
                      <Badge className={getTVRankingColor(project.tv_ranking_tier)}>
                        {project.tv_ranking_tier}
                      </Badge>
                    )}
                  </div>

                  {/* Category Density Indicators */}
                  {Object.keys(project.category_density).length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {Object.entries(project.category_density).slice(0, 3).map(([category, density]) => (
                        <Badge key={category} variant="outline" className="text-xs">
                          {category}: {Math.round(density * 100)}%
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Heuristic Tags */}
                  {project.heuristic_tags && project.heuristic_tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {project.heuristic_tags.slice(0, 2).map((tag, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        viewPackage(project.id);
                      }}
                    >
                      View Package
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="px-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        openProjectDetails(project);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {projects.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Search className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No eligible projects found</h3>
            <p className="text-gray-600">
              Try adjusting your campaign requirements or filters to see more inventory.
            </p>
          </div>
        )}
      </div>

      {/* Project Details Modal */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          {selectedProject && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{selectedProject.title}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/artie-score/${selectedProject.id}`, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Artie Score PDF
                  </Button>
                </DialogTitle>
              </DialogHeader>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Panel - Project Details */}
                <div className="space-y-4">
                  {selectedProject.hero_image_url && (
                    <img 
                      src={selectedProject.hero_image_url} 
                      alt={selectedProject.title}
                      className="w-full aspect-video object-cover rounded-lg"
                    />
                  )}
                  
                  <div>
                    <h4 className="font-medium mb-2">Logline</h4>
                    <p className="text-gray-600">{selectedProject.logline}</p>
                  </div>

                  {selectedProject.reason_to_believe && (
                    <div>
                      <h4 className="font-medium mb-2">Reason to Believe</h4>
                      <p className="text-blue-600">{selectedProject.reason_to_believe}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium mb-2">Creator</h4>
                      <p className="text-gray-600">{selectedProject.creator}</p>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Vertical</h4>
                      <Badge variant="outline">{selectedProject.vertical}</Badge>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium mb-2">90-Day Reach</h4>
                      <p className="text-gray-600">{formatNumber(selectedProject.forecasted_reach_90d)}</p>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Production Status</h4>
                      <Badge variant="outline">{selectedProject.production_status.replace('_', ' ')}</Badge>
                    </div>
                  </div>
                </div>

                {/* Right Panel - Metadata and Alignment */}
                <div className="space-y-4">
                  {selectedProject.tv_ranking_tier && (
                    <div>
                      <h4 className="font-medium mb-2">TV Ranking</h4>
                      <Badge className={getTVRankingColor(selectedProject.tv_ranking_tier)}>
                        {selectedProject.tv_ranking_tier} Tier
                      </Badge>
                    </div>
                  )}

                  {selectedProject.heuristic_tags && selectedProject.heuristic_tags.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Heuristic Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProject.heuristic_tags.map((tag, index) => (
                          <Badge key={index} variant="secondary">{tag}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {Object.keys(selectedProject.category_density).length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Category Density</h4>
                      <div className="space-y-2">
                        {Object.entries(selectedProject.category_density).map(([category, density]) => (
                          <div key={category} className="flex justify-between items-center">
                            <span className="text-sm">{category}</span>
                            <span className="text-sm font-medium">{Math.round(density * 100)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {campaign && (
                    <div>
                      <h4 className="font-medium mb-2">Campaign Alignment</h4>
                      <div className="space-y-2">
                        {selectedProject.demographic_alignment && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Demographic Match</span>
                            <span className="text-sm font-medium">{Math.round(selectedProject.demographic_alignment * 100)}%</span>
                          </div>
                        )}
                        {selectedProject.genre_alignment && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Genre Match</span>
                            <span className="text-sm font-medium">{Math.round(selectedProject.genre_alignment * 100)}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="pt-4">
                    <Button 
                      className="w-full"
                      onClick={() => {
                        setSelectedProject(null);
                        viewPackage(selectedProject.id);
                      }}
                    >
                      View Package for This Project
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrowseInventoryPage;